<template>
    <!-- app-level page: own top bar, build default colors and the webapp background instead of the event branding -->
    <div id="settings_page">
        <HLAppBar @back="onBack"/>
        <v-main :style="{ background: webappBackground }">
            <v-container class="settings-container" :class="{ 'settings-container--split': isSplitView }">
                <h1 class="settings-title pl-1" :style="{ color: appTheme.backgroundColor }">{{ $t('settings') }}</h1>
                <v-row>
                    <!-- section list and actions; the version below them like the native screen -->
                    <v-col v-if="view.showMenu" cols="12" md="5">
                        <EspSettingsMenuCard
                            :items="menu.sections"
                            :selected="isSplitView ? view.section : null"
                            :textColor="appTheme.textColor"
                            :iconColor="appTheme.footerIconColor"
                            :backgroundColor="appTheme.backgroundColor"
                            @select="onSelectSection"
                        />
                        <EspSettingsMenuCard
                            class="mt-4"
                            :items="menu.actions"
                            :textColor="appTheme.textColor"
                            :iconColor="appTheme.footerIconColor"
                            :backgroundColor="appTheme.backgroundColor"
                            @select="onAction"
                        />
                        <div class="text-center pt-3 text-caption" :style="{ color: appTheme.backgroundColor }">
                            VERSION {{ version }}
                        </div>
                    </v-col>
                    <!-- open section -->
                    <v-col v-if="view.showDetail" cols="12" md="7">
                        <v-card flat rounded="lg" :color="appTheme.backgroundColor">
                            <div class="settings-detail">
                                <HLEventList v-if="view.section === 'events'" hideLogo @next="onSelectedEvent"/>
                                <EspSettingsLanguageList
                                    v-else-if="view.section === 'language'"
                                    :color="appTheme.textColor"
                                    :iconColor="appTheme.footerIconColor"
                                    :activeColor="appTheme.btnColorPrimary"
                                    :backgroundColor="appTheme.backgroundColor"
                                    @change="onChangeLang"
                                />
                                <HLLegalFrame v-else-if="view.section === 'imprint'" kind="imprint"/>
                                <HLLegalFrame v-else-if="view.section === 'privacy'" kind="privacy_policy"/>
                            </div>
                        </v-card>
                    </v-col>
                </v-row>
                <HLLoadingMaskOverlay v-show="event_single_native_loading"/>
            </v-container>
        </v-main>
    </div>
</template>

<script>
import CONFIG from '@/constants/config.js';
import { mapState } from 'vuex';
import { layoutMixin } from '@/utils/layout-mixin';
import { eventMixin } from '@/utils/event-mixin';
import { settingsMenu, resolveSettingsView } from '@/utils/settings-menu.mjs';
import HLAppBar from '@/components/ui/HLAppBar.vue';
import HLLegalFrame from '@/components/ui/HLLegalFrame.vue';
import HLLoadingMaskOverlay from '@/components/ui/HLLoadingMaskOverlay.vue';
import HLEventList from '@/components/dashboard/HLEventList.vue';
import EspSettingsMenuCard from '@/components/settings/EspSettingsMenuCard.vue';
import EspSettingsLanguageList from '@/components/settings/EspSettingsLanguageList.vue';

export default {
    mixins: [layoutMixin, eventMixin],
    components: {
        HLAppBar,
        HLLegalFrame,
        HLLoadingMaskOverlay,
        HLEventList,
        EspSettingsMenuCard,
        EspSettingsLanguageList,
    },
    computed: {
        ...mapState('events', ['event_single_native_loading']),
        version() {
            return CONFIG.APP_VERSION;
        },
        disabledLogout() {
            return this.parseBoolean(CONFIG.DISABLE_LOGOUT, false);
        },
        // the app's own background image, even if the event brands the body with a custom one
        webappBackground() {
            return `url('${this.defaultBackgroundImageUrl}') no-repeat center / cover`;
        },
        // list and detail side by side from the md breakpoint on (desktop and landscape tablets, the native tablet case)
        isSplitView() {
            return this.isMdAndUpDevice;
        },
        isSettingsRoute() {
            return this.$route.name === 'settings';
        },
        view() {
            return resolveSettingsView({ section: this.$route.params.section, split: this.isSplitView });
        },
        menu() {
            const { sections, actions } = settingsMenu({ disableLogout: this.disabledLogout });
            return { sections: sections.map(this.withTitle), actions: actions.map(this.withTitle) };
        },
    },
    watch: {
        // keeps the route in line with the view (split needs a section, stacked list none); the
        // instance stays alive in keep-alive, so only while the settings route is the current one
        view: {
            handler(view) {
                if (this.isSettingsRoute && view.redirect !== null) {
                    this.navigateTo({ path: this.settingsPath(view.redirect) }, true);
                }
            },
            immediate: true,
        },
    },
    methods: {
        withTitle(item) {
            const params = { eventPlural: this.$tc('event', 2), eventInLine: this.$tc('eventInLine') };
            return { key: item.key, title: this.$t(item.label, params) };
        },
        settingsPath(section) {
            const base = `/event/${this.$route.params.id}/settings`;
            return section ? `${base}/${section}` : base;
        },
        onSelectSection(key) {
            this.navigateTo({ path: this.settingsPath(key) });
        },
        onAction(key) {
            if (key === 'logout') {
                this.navigateTo({ path: '/logout' });
            } else {
                this.goToEvent(this.selectedEventId);
            }
        },
        // a stacked detail goes back to the list, everything else back to the event
        onBack() {
            if (!this.isSplitView && this.view.showDetail) {
                this.navigateTo({ path: this.settingsPath('') });
            } else {
                this.goToEvent(this.selectedEventId);
            }
        },
        onSelectedEvent(id) {
            this.setThemeColors(id);
            this.goToEvent(id);
        },
        onChangeLang() {
            setTimeout(() => {
                this.$store.dispatch('events/refresh');
                if (this.selectedEventId) {
                    this.$store.dispatch('spots/getSpotsByEventId', this.selectedEventId);
                }
            }, 500);
        },
    },
};
</script>

<style lang="scss" scoped>
// grows inside v-app's flex column so v-main covers the viewport down to the bottom edge
#settings_page {
    display: flex;
    flex-direction: column;
    flex: 1 0 auto;
}

.settings-container {
    position: relative;
    max-width: 700px;

    &--split {
        max-width: 1100px;
    }
}

.settings-title {
    margin-bottom: 8px;
}

// own scroll area so the event list's sticky actions and the legal iframe get a definite height
.settings-detail {
    position: relative;
    height: calc(100vh - 230px);
    min-height: 360px;
    overflow-y: auto;
}
</style>
